import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';

/**
 * TimeSlider — Ventusky-style timeline with date picker + hourly scrubber.
 * Range: Past 7 days ← NOW → Future 24 hours
 */
const TimeSlider = ({ timeOffset, setTimeOffset, visible }) => {
    const minH = -168;  // 7 days back
    const maxH = 168;   // 7 days forward

    // Anchor time to the absolute top of the current hour
    const getBaseTime = () => {
        const d = new Date();
        d.setMinutes(0, 0, 0);
        return d;
    };

    // Current target date/time based on offset
    const targetDate = useMemo(() => {
        return new Date(getBaseTime().getTime() + timeOffset * 3600 * 1000);
    }, [timeOffset]);



    // Time label logic replaced by inline input value

    // Generate hour marks for the timeline bar
    const hourMarks = useMemo(() => {
        const marks = [];
        // Show 6-hour interval labels on the bar
        for (let h = 0; h <= 24; h += 3) {
            const hour = h % 24;
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const h12 = hour % 12 || 12;
            marks.push({ position: (h / 24) * 100, label: `${h12}${ampm}` });
        }
        return marks;
    }, []);

    if (!visible) return null;

    // Get the hour-of-day from the targetDate for the inner hourly slider
    const currentHour = targetDate.getHours();
    
    // Jump to previous day (same hour)
    const prevDay = () => setTimeOffset(prev => Math.max(minH, prev - 24));
    // Jump to next day (same hour)
    const nextDay = () => setTimeOffset(prev => Math.min(maxH, prev + 24));
    // Reset to now
    const resetNow = () => setTimeOffset(0);

    // Change hour within the current day
    const handleHourChange = (e) => {
        const newHour = parseInt(e.target.value);
        const nextDate = new Date(targetDate);
        nextDate.setHours(newHour, 0, 0, 0);
        const diffMs = nextDate.getTime() - getBaseTime().getTime();
        const newOffset = Math.round(diffMs / (3600 * 1000));
        setTimeOffset(Math.max(minH, Math.min(maxH, newOffset)));
    };

    // Change entire date via <input type="date">
    const handleDateChange = (e) => {
        if (!e.target.value) return; // Prevent NaN if user clears the input
        const picked = new Date(e.target.value + 'T' + String(targetDate.getHours() || 0).padStart(2, '0') + ':00:00');
        const diffMs = picked.getTime() - getBaseTime().getTime();
        const newOffset = Math.round(diffMs / (3600 * 1000));
        setTimeOffset(Math.max(minH, Math.min(maxH, newOffset)));
    };

    // Format date for the input value
    const inputDateVal = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;

    // Min/max dates for the date picker
    const minDate = new Date(getBaseTime().getTime() + minH * 3600 * 1000);
    const maxDate = new Date(getBaseTime().getTime() + maxH * 3600 * 1000);
    const minDateStr = `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, '0')}-${String(minDate.getDate()).padStart(2, '0')}`;
    const maxDateStr = `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}-${String(maxDate.getDate()).padStart(2, '0')}`;

    return (
        <div className="time-slider-container" style={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(16px)',
            borderRadius: '14px',
            padding: '8px 14px 10px 14px',
            boxShadow: '0 8px 32px rgba(74, 122, 40, 0.15), 0 0 0 1px rgba(118, 171, 63, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            width: '520px',
            maxWidth: '92vw',
            fontFamily: "'Inter', sans-serif",
        }}>
            {/* ── Top row: Previous | Date Picker | Reset | Next ── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                gap: '6px',
            }}>
                {/* Previous Day */}
                <button onClick={prevDay} title="Previous Day" style={btnStyle}>
                    <ChevronLeft size={14} />
                    <ChevronLeft size={14} style={{ marginLeft: -10 }} />
                </button>

                {/* Date Picker */}
                <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                }}>
                    <input
                        type="date"
                        value={inputDateVal}
                        min={minDateStr}
                        max={maxDateStr}
                        onChange={handleDateChange}
                        style={{
                            background: 'rgba(118, 171, 63, 0.08)',
                            border: '1px solid rgba(118, 171, 63, 0.2)',
                            color: '#4a7a28',
                            borderRadius: '8px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif",
                            cursor: 'pointer',
                            outline: 'none',
                            colorScheme: 'light',
                        }}
                    />
                </div>

                {/* Time Picker */}
                <input
                    type="time"
                    value={`${String(targetDate.getHours()).padStart(2, '0')}:00`}
                    step="3600"
                    onChange={(e) => {
                        if (!e.target.value) return;
                        const [h] = e.target.value.split(':');
                        const newHour = parseInt(h);
                        const nextDate = new Date(targetDate);
                        nextDate.setHours(newHour, 0, 0, 0);
                        const diffMs = nextDate.getTime() - getBaseTime().getTime();
                        const newOffset = Math.round(diffMs / (3600 * 1000));
                        setTimeOffset(Math.max(minH, Math.min(maxH, newOffset)));
                    }}
                    style={{
                        background: 'rgba(118, 171, 63, 0.08)',
                        border: '1px solid rgba(118, 171, 63, 0.2)',
                        borderRadius: '8px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#4a7a28',
                        cursor: 'pointer',
                        outline: 'none',
                        fontFamily: "'Inter', sans-serif"
                    }}
                />

                {/* Reset button */}
                <button onClick={resetNow} title="Reset to Now" style={{
                    ...btnStyle,
                    background: timeOffset === 0 ? 'rgba(118, 171, 63, 0.12)' : 'rgba(0,0,0,0.03)',
                    border: timeOffset === 0 ? '1px solid rgba(118, 171, 63, 0.3)' : '1px solid rgba(0,0,0,0.06)',
                    color: timeOffset === 0 ? '#4a7a28' : '#6B7280',
                    gap: '4px',
                    padding: '4px 10px',
                    fontSize: '10px',
                    fontWeight: 600,
                }}>
                    <RotateCcw size={11} />
                    Reset
                </button>

                {/* Next Day */}
                <button onClick={nextDay} title="Next Day" style={btnStyle}>
                    <ChevronRight size={14} />
                    <ChevronRight size={14} style={{ marginLeft: -10 }} />
                </button>
            </div>

            {/* ── Bottom row: Hourly timeline bar ── */}
            <div style={{
                width: '100%',
                position: 'relative',
                paddingTop: '2px',
            }}>
                {/* Hour slider */}
                <input
                    type="range"
                    min={0}
                    max={23}
                    value={currentHour}
                    onChange={handleHourChange}
                    className="timeline-range"
                    style={{
                        width: '100%',
                        height: '8px',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        borderRadius: '4px',
                        outline: 'none',
                        cursor: 'pointer',
                        background: `linear-gradient(to right, 
                            #F1F5F9 0%, 
                            #E2E8F0 20%, 
                            #CBD5E1 35%,
                            #76AB3F 50%, 
                            #CBD5E1 65%,
                            #E2E8F0 80%, 
                            #F1F5F9 100%)`,
                    }}
                />

                {/* Hour tick labels */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '2px 2px 0',
                }}>
                    {hourMarks.map((mark, i) => (
                        <span key={i} style={{
                            fontSize: '8px',
                            color: '#64748B',
                            fontWeight: 500,
                            userSelect: 'none',
                        }}>
                            {mark.label}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Shared button style
const btnStyle = {
    background: 'rgba(0,0,0,0.03)',
    border: '1px solid rgba(0,0,0,0.06)',
    color: '#6B7280',
    borderRadius: '8px',
    padding: '5px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.15s',
    fontFamily: "'Inter', sans-serif",
};

export default TimeSlider;
